import sanitize from "sanitize-html";

/**
 * 净化来自后端 / CMS / MTS 的富文本，再交给 dangerouslySetInnerHTML。
 *
 * 为什么用 sanitize-html 而不是 DOMPurify：这些内容是 SSR 渲染的，只在浏览器端
 * 净化没有意义 —— 服务端吐出的 HTML 若含 <script> 或 onerror，浏览器解析首屏时就已
 * 执行，等不到客户端代码跑。所以必须两端都能跑。
 * DOMPurify 需要 DOM，服务端要靠 isomorphic-dompurify 引 jsdom，而当前 jsdom@27
 * 要求 Node 20+（本机 Node 18.20.5 直接崩，CI 的 Node 版本用的是外部模板、不可见）。
 * sanitize-html 是纯 JS 解析（htmlparser2），不依赖 DOM 也不依赖 Node 版本特性。
 *
 * 配置取向：**尽量不动合法排版**。白名单按 CMS / 商品富文本实际会用到的标签与属性
 * 给足（排版、列表、表格、图片、视频、class/style），危险面按标签与协议剥除。
 *
 * ⚠️ 不要用它净化本项目自己写的内联脚本与样式
 * （QuickTracking、SEO 的 GTM、_document 的 antd extractStyle）——
 * 那几处是自有可信内容，净化会直接打断埋点与首屏样式。
 */

const OPTIONS: sanitize.IOptions = {
  allowedTags: [
    // 排版
    "p", "br", "hr", "div", "span", "section", "article", "blockquote", "pre", "code",
    "h1", "h2", "h3", "h4", "h5", "h6",
    // 行内样式
    "strong", "b", "em", "i", "u", "s", "del", "ins", "mark", "small", "sup", "sub", "font",
    // 列表
    "ul", "ol", "li", "dl", "dt", "dd",
    // 表格
    "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
    // 媒体与链接
    "a", "img", "picture", "source", "video", "audio", "figure", "figcaption",
  ],
  allowedAttributes: {
    "*": ["class", "style", "id", "title", "dir", "lang", "align"],
    a: ["href", "target", "rel", "name"],
    img: ["src", "srcset", "sizes", "alt", "width", "height", "loading", "decoding"],
    source: ["src", "srcset", "sizes", "type", "media"],
    video: ["src", "poster", "controls", "autoplay", "muted", "loop", "playsinline", "width", "height", "preload"],
    audio: ["src", "controls", "autoplay", "muted", "loop", "preload"],
    table: ["border", "cellpadding", "cellspacing", "width"],
    th: ["colspan", "rowspan", "scope", "width"],
    td: ["colspan", "rowspan", "width"],
    col: ["span", "width"],
    font: ["color", "size", "face"],
  },
  // 只放行安全协议；img 额外允许 data:（富文本里常见内嵌小图）
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: { img: ["http", "https", "data"] },
  allowProtocolRelative: true,
  // script / style 标签连内容一起丢弃，不要把 JS 当文本留在页面上
  nonTextTags: ["script", "style", "textarea", "option", "noscript"],
  transformTags: {
    // 外链补 rel，避免 tabnabbing
    a: (tagName, attribs) => ({
      tagName,
      attribs: attribs.target ? { ...attribs, rel: "noopener noreferrer" } : attribs,
    }),
  },
};

/**
 * @param html 远端富文本，可能为 undefined / null
 * @returns 净化后的 HTML 字符串；输入为空时返回空串
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return "";
  return sanitize(html, OPTIONS);
}
